import cn from 'clsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useContext, useRef } from 'react';
import Typography from '@material-ui/core/Typography';
import { PromptContext, usePrompts } from '@eas/common-web';
import { CalendarContext } from '@modules/planner/calendar/calendar-context';
import { BulkOperationsContext } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-context';
import { useBulkOperationsTaskStyles } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-styles';
import { getTaskBoundaries } from '@modules/planner/calendar/utils';
import { getSecondsInPixel } from '@modules/planner/calendar/utils/get-seconds-in-pixel';
import { getTaskStart } from '@modules/planner/calendar/utils/get-task-start';
import { Draggable } from '@modules/planner/dnd/draggable';
import { useIsKeyPressed } from '@modules/planner/dnd/utils/is-key-pressed';
import { getMultitaskBusinessWorkOrderIds } from '@modules/planner/work-order-id-utils';
import {
  MultitaskWorkOrderMenu,
  WorkOrderMenuHandle,
} from '@modules/planner/work-order-menu/work-order-menu';
import { MultitaskDetailDialog } from '@composite/multitask/multitask-detail-dialog';
import { useMultitaskStyles } from '@composite/multitask/multitask-styles';
import { Multitask } from '@composite/multitask/types';
import { WorkOrder } from '@models';

dayjs.extend(duration);

const getMultitaskKey = (multitask: Multitask) =>
  `MULTITASK_${multitask.day}_${multitask.unitId}`;

export function MultiTask({
  multitask,
  unitName,
}: {
  multitask: Multitask;
  unitName: string;
}) {
  const classes = useMultitaskStyles();
  const bulkClasses = useBulkOperationsTaskStyles();
  const multitaskMenuRef = useRef<WorkOrderMenuHandle>(null);
  const { settings, currentDate, dataSource, workOrderTableRef } =
    useContext(CalendarContext);
  const { testPrompt } = useContext(PromptContext);

  const { bulkEditModeOn, addToSelection, selected, removeFromSelection } =
    useContext(BulkOperationsContext);
  const secondsInPixel = getSecondsInPixel(settings);

  const { workOrderStart, workOrderEnd } = getTaskBoundaries(
    currentDate,
    settings,
    {
      startTime: `${multitask.day}T00:00`,
      endTime: `${multitask.day}T23:59`,
    } as WorkOrder
  );

  // multitasks are always all day long
  const visibleHours = settings.endingHour - settings.startingHour;
  const taskSize = (3600 * visibleHours - 1) / secondsInPixel;

  usePrompts(
    [
      {
        key: getMultitaskKey(multitask),
        dialogTitle: `Množina úloh - ${unitName} (${dayjs(multitask.day).format(
          'DD.MM.YYYY'
        )} )`,
        dialogShowConfirm: false,
        dialogWidth: 900,
        dialogText: (
          <MultitaskDetailDialog
            unitName={unitName}
            multitask={multitask}
            refetch={() => {
              dataSource?.yAxis?.refreshData?.();
              workOrderTableRef.current?.source?.reset?.();
              workOrderTableRef.current?.source?.loadMore?.();
            }}
          />
        ),
      },
    ],
    [multitask, dataSource.yAxis.refreshData, workOrderTableRef.current?.source]
  );

  const { isKeyPressed } = useIsKeyPressed('Control');

  if (taskSize === 0) {
    return null;
  }

  const taskStart = getTaskStart(
    currentDate,
    workOrderStart,
    workOrderEnd,
    settings
  );

  const taskHeightBase = settings.rowHeight * 0.9;

  const getIsDraggable = () => {
    if (!isKeyPressed) return false;

    if (bulkEditModeOn) {
      if (selected.some((s) => s.unitId !== multitask.unitId)) return false;
    }

    return true;
  };

  const isDraggable = getIsDraggable();

  const multitaskKey = getMultitaskKey(multitask);
  const multitaskWorkOrderIds = getMultitaskBusinessWorkOrderIds(multitask);

  return (
    <>
      <div
        onClick={() => {
          if (bulkEditModeOn) {
            if (
              selected
                .filter((s) => s.isMultiTask)
                .find((s) => s.id === multitaskKey)
            ) {
              removeFromSelection(multitaskKey);
            } else {
              addToSelection({
                id: multitaskKey,
                isMultiTask: true,
                unitId: multitask.unitId,
                multitaskWoIds: multitask.workOrderRefs.map((x) => x.id),
                multitaskWorkOrderIds,
              });
            }
          } else {
            testPrompt({
              key: multitaskKey,
              submitCallback: () => {},
            });
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          multitaskMenuRef.current?.open(e);
        }}
        className={cn(classes.root, {
          [bulkClasses.isSelected]:
            bulkEditModeOn &&
            selected
              .filter((s) => s.isMultiTask)
              .find((s) => s.id === multitaskKey),
        })}
        style={{
          left: taskStart,
          width: taskSize,
          height: taskHeightBase,
          top: settings.rowHeight * 0.05,
          opacity: isKeyPressed ? (isDraggable ? 1 : 0.3) : 1,
          cursor: isKeyPressed
            ? isDraggable
              ? 'pointer'
              : 'not-allowed'
            : 'pointer',
        }}
      >
        <Draggable
          id={multitaskKey}
          data={multitask.workOrderRefs.filter((x) => !x.locked)}
          disabled={!isDraggable}
          className={classes.content}
        >
          <div className={classes.bullet} />
          <Typography variant="body2" className={classes.title}>
            Množina úloh (<b>{multitask?.workOrderRefs?.length} </b>pracovných
            príkazov)
          </Typography>
        </Draggable>
      </div>
      <MultitaskWorkOrderMenu ref={multitaskMenuRef} multitask={multitask} />
    </>
  );
}
